/**
 * Visual body block of the pharma questionnaire page: toolbar on top,
 * documents pane on the left, questions pane on the right.
 *
 * Pulled out of `pharmaQuestionaireLayout.tsx` so the layout stays a
 * thin orchestrator. This component is intentionally presentational —
 * it owns no state and forwards every interaction to the parent.
 *
 * @module pharmaQuestionaire/components/PharmaQuestionnaireBody
 */
import React, { ComponentProps } from "react";
import { EvaluationDecision } from "../../../components/modals/predictionSummaryModal/types";
import { PharmaBranchedQuestionnaire } from "../../../data-model/cmmMedicalNeccessityQuestionnaire";
import DocumentsPane from "./DocumentsPane";
import PharmaQuestionsWithAllBranching from "./PharmaQuestionsWithAllBranching";
import QuestionnaireToolbar from "./QuestionnaireToolbar";

type ToolbarProps = ComponentProps<typeof QuestionnaireToolbar>;

interface PharmaQuestionnaireBodyProps {
  singleCmmOrderData: ToolbarProps["singleCmmOrderData"];
  documents: File[];
  isFetchingDoc: boolean;
  errorInFetching: boolean;
  mergedPdfToRender: File | null;
  questionnaire: PharmaBranchedQuestionnaire | null;
  initialQuestionnaire: PharmaBranchedQuestionnaire | null;
  onValidityChange: (isValid: boolean) => void;
  onWorkflowDocumentChange: (document: PharmaBranchedQuestionnaire) => void;
  onManageAttachments: () => void;
  /** Opens the Prediction Summary modal from the toolbar. */
  onPredictionSummary: () => void;
  /** Latest MN judge decision for toolbar preview badges. */
  mnJudgeDecision?: EvaluationDecision | null;
  /** Latest MN judge confidence percent for toolbar preview badges. */
  mnJudgeConfidence?: number | null;
}

/**
 * Presentational questionnaire body: toolbar, documents pane, questions pane.
 *
 * @param props - Order data, documents, questionnaire, and interaction handlers
 * @returns Questionnaire body layout
 */
const PharmaQuestionnaireBody: React.FC<PharmaQuestionnaireBodyProps> = ({
  singleCmmOrderData,
  documents,
  isFetchingDoc,
  errorInFetching,
  mergedPdfToRender,
  questionnaire,
  initialQuestionnaire,
  onValidityChange,
  onWorkflowDocumentChange,
  onManageAttachments,
  onPredictionSummary,
  mnJudgeDecision = null,
  mnJudgeConfidence = null,
}) => (
  <div className="pharma-questionaire-form__body flex flex-1 flex-col overflow-hidden rounded bg-white p-4 pb-0">
    <QuestionnaireToolbar
      singleCmmOrderData={singleCmmOrderData}
      onManageAttachments={onManageAttachments}
      onPredictionSummary={onPredictionSummary}
      mnJudgeDecision={mnJudgeDecision}
      mnJudgeConfidence={mnJudgeConfidence}
    />
    <div className="pharma-questionaire-form__body-content flex flex-1 overflow-hidden rounded-t-lg border bg-[#F7F9FA]">
      <div className="documents-pane w-2/5">
        <DocumentsPane
          isFetchingDoc={isFetchingDoc}
          errorInFetching={errorInFetching}
          documents={documents}
          mergedPdfToRender={mergedPdfToRender}
        />
      </div>
      <div className="questions-pane w-3/5">
        <PharmaQuestionsWithAllBranching
          questionnaire={questionnaire ?? ({} as PharmaBranchedQuestionnaire)}
          onValidityChange={onValidityChange}
          onWorkflowDocumentChange={onWorkflowDocumentChange}
          initialQuestionnaire={
            initialQuestionnaire ?? ({} as PharmaBranchedQuestionnaire)
          }
        />
      </div>
    </div>
  </div>
);

export default PharmaQuestionnaireBody;
